import { checkEnabledProcess } from "components/GuardianAlert";
import { usePermissions } from "contexts/permissionCtx";
import { AccessArgs } from "types/accessRules";

import { useUser } from "../hooks/useUser";

type AccessCheckResult = {
  accessDenied: boolean;
};

const accessDeniedObj = { accessDenied: true };
const accessGrantedObj = { accessDenied: false };

export const accessRules: Record<string, (args: AccessArgs) => AccessCheckResult> = {
  SelfCertificationPage: ({ lastSelfCertStatus }) => {
    const { data: user } = useUser();
    const { permissions } = usePermissions();

    if (!user) return accessDeniedObj;

    const { kyc, virtualAssets, selfCertification } = user;
    const accessDenied =
      selfCertification.currentStatus === "accepted" ||
      lastSelfCertStatus === "pending" ||
      !checkEnabledProcess([kyc.currentStatus, virtualAssets.currentStatus], undefined) ||
      permissions?.isEDDBlocking ||
      permissions?.isBlockedByPromotionRequest;

    if (accessDenied) {
      return accessDeniedObj;
    }

    return accessGrantedObj;
  },

  FullCertificationPage: ({ lastFullCertStatus }) => {
    const { data: user } = useUser();
    const { permissions } = usePermissions();

    if (!user) return accessDeniedObj;

    const { kyc, virtualAssets, selfCertification, isHighNetWorth } = user;

    const accessDenied =
      lastFullCertStatus === "pending" ||
      !checkEnabledProcess(
        [kyc.currentStatus, virtualAssets.currentStatus, selfCertification.currentStatus],
        undefined
      ) ||
      permissions?.isEDDBlocking ||
      !isHighNetWorth;

    if (accessDenied) {
      return accessDeniedObj;
    }

    return accessGrantedObj;
  },

  UKRegulationsPage: () => {
    const { data: user } = useUser();
    const {
      permissions: { isPromotionRequestProcessing, isRemediationRequired },
    } = usePermissions();

    if (!user) return accessDeniedObj;

    const accessDenied = !isPromotionRequestProcessing || isRemediationRequired || !user.promotionRequest.startable;

    if (accessDenied) {
      return accessDeniedObj;
    }

    return accessGrantedObj;
  },

  Edd: () => {
    const { data: user } = useUser();

    if (!user) return accessDeniedObj;

    const accessDenied = user.edd.lastStatus !== "draft";

    if (accessDenied) {
      return accessDeniedObj;
    }

    return accessGrantedObj;
  },

  VAPage: ({ lastVAStatus }) => {
    const { data: user } = useUser();
    const { permissions } = usePermissions();

    if (!user) return accessDeniedObj;

    const accessDenied =
      lastVAStatus === "pending" ||
      (permissions?.isBlockedByPromotionRequest && !permissions?.isRemediationRequired) ||
      (!checkEnabledProcess([user?.kyc.currentStatus], undefined) &&
        !checkEnabledProcess([user?.kyc.lastStatus], undefined));

    if (accessDenied) {
      return accessDeniedObj;
    }

    return accessGrantedObj;
  },

  KYC: ({ lastKYCStartable }) => {
    const { data: user } = useUser();
    const { permissions } = usePermissions();

    if (!user) return accessDeniedObj;

    const accessDenied = !lastKYCStartable || permissions?.isRemediationRequired;

    if (accessDenied) {
      return accessDeniedObj;
    }

    return accessGrantedObj;
  },
};
